import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { C } from '../utils/theme.js';
import Card from './Card.jsx';
import StatBox from './StatBox.jsx';
import { t } from '../utils/i18n.js';
import { gridToLatLon, latLonToGrid, isValidGrid } from '../utils/gridLocator.js';
import { addDarkBasemap } from '../utils/mapTiles.js';

// The location editor is collapsed into a thin status bar and only expands on
// demand — pattern used by HamClock/DXRadar/HamTab. The storage key remembers the
// last manually picked field site (POTA/SOTA/Field Day) across visits.
var LOC_STORAGE_KEY = "hamexpert_fieldops_loc";
function readStoredLoc() {
  try {
    var o = JSON.parse(localStorage.getItem(LOC_STORAGE_KEY) || "null");
    if (o && isFinite(o.lat) && isFinite(o.lon)) return o;
  } catch (e) {}
  return null;
}

var WEATHER_EMOJI = {
  "01d": "☀️", "01n": "🌙",
  "02d": "⛅", "02n": "⛅",
  "03d": "☁️", "03n": "☁️",
  "04d": "☁️", "04n": "☁️",
  "09d": "🌧️", "09n": "🌧️",
  "10d": "🌦️", "10n": "🌦️",
  "11d": "⚡", "11n": "⚡",
  "13d": "❄️", "13n": "❄️",
  "50d": "🌫️", "50n": "🌫️"
};

function windDirLabel(deg) {
  var dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round((deg || 0) / 22.5) % 16];
}

function condColor(cond) {
  if (!cond) return C.t3;
  var c = cond.toLowerCase();
  if (c === "good") return "#00ff88";
  if (c === "fair") return "#eab308";
  return "#f43f5e";
}

function kColor(k) {
  if (k <= 2) return "#00ff88";
  if (k <= 4) return "#eab308";
  return "#f43f5e";
}

function aColor(a) {
  if (a <= 7) return "#00ff88";
  if (a <= 15) return "#eab308";
  return "#f43f5e";
}

function sfiColor(s) {
  if (s >= 100) return "#00ff88";
  if (s >= 70) return "#eab308";
  return "#f43f5e";
}

function mufColor(m) {
  if (m >= 21) return "#00ff88";
  if (m >= 14) return "#eab308";
  return "#f43f5e";
}

function activityColor(n) {
  if (n >= 20) return "#00ff88";
  if (n > 0) return C.a1;
  return C.t3;
}

export default function FieldOpsView(props) {
  var lang = props.lang;
  var geoInfo = props.geoInfo;

  var solarSt = useState(null); var solar = solarSt[0]; var setSolar = solarSt[1];
  var propSt = useState(null); var prop = propSt[0]; var setProp = propSt[1];
  var weatherSt = useState(null); var weather = weatherSt[0]; var setWeather = weatherSt[1];
  var loadingSt = useState(true); var loading = loadingSt[0]; var setLoading = loadingSt[1];
  var lastUpdateSt = useState(null); var lastUpdate = lastUpdateSt[0]; var setLastUpdate = lastUpdateSt[1];

  // Location the conditions are shown for. Priority: last manual pick (localStorage)
  // → profile grid → IP geo → hardcoded fallback. The profile grid (not the
  // VPN-affected IP) is the sensible default; a manual pick always wins.
  function computeInitialLoc() {
    var stored = readStoredLoc();
    if (stored) return stored;
    if (props.myGrid && isValidGrid(props.myGrid)) {
      var c = gridToLatLon(props.myGrid);
      if (c) return { lat: c.lat, lon: c.lon, grid: String(props.myGrid).trim(), label: "" };
    }
    if (geoInfo && geoInfo.lat) return { lat: geoInfo.lat, lon: geoInfo.lon, grid: geoInfo.grid || "", label: geoInfo.city || "" };
    return { lat: -27.1, lon: -48.9, grid: "GG54mc", label: "Brusque" };
  }
  var locSt = useState(computeInitialLoc); var loc = locSt[0]; var setLoc = locSt[1];
  // True once the operator has chosen a location by map/grid/city/GPS. While false the
  // location keeps tracking the profile grid as it loads in asynchronously.
  var userPickedRef = useRef(!!readStoredLoc());

  // Editor panel collapsed by default — the whole point of V2.
  var locOpenSt = useState(false); var locOpen = locOpenSt[0]; var setLocOpen = locOpenSt[1];

  var gpsBusySt = useState(false); var gpsBusy = gpsBusySt[0]; var setGpsBusy = gpsBusySt[1];
  var gpsErrSt = useState(false); var gpsErr = gpsErrSt[0]; var setGpsErr = gpsErrSt[1];

  var gridInSt = useState(loc.grid || ""); var gridIn = gridInSt[0]; var setGridIn = gridInSt[1];
  var cityQSt = useState(""); var cityQ = cityQSt[0]; var setCityQ = cityQSt[1];
  var cityResSt = useState(null); var cityRes = cityResSt[0]; var setCityRes = cityResSt[1];
  var citySearchingSt = useState(false); var citySearching = citySearchingSt[0]; var setCitySearching = citySearchingSt[1];

  var miniRef = useRef(null);
  var miniMapRef = useRef(null);
  var miniMarkerRef = useRef(null);
  var readySt = useState(!!window.L); var ready = readySt[0]; var setReady = readySt[1];

  // Leaflet is loaded on demand, and in V2 only once the editor panel is opened —
  // operators that never change location don't download the library on this view.
  useEffect(function() {
    if (!locOpen) return;
    if (window.L) { setReady(true); return; }
    if (document.getElementById("lf-css")) {
      var poll = setInterval(function() { if (window.L) { setReady(true); clearInterval(poll); } }, 200);
      return function() { clearInterval(poll); };
    }
    var css = document.createElement("link");
    css.id = "lf-css"; css.rel = "stylesheet";
    css.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(css);
    var js = document.createElement("script");
    js.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    js.onload = function() { setReady(true); };
    document.head.appendChild(js);
  }, [locOpen]);

  // Commit a manual location pick: normalize the grid, remember it, and stop
  // auto-tracking the profile grid.
  var pickLoc = useCallback(function(lat, lon, grid, label) {
    userPickedRef.current = true;
    var g = grid || latLonToGrid(lat, lon);
    var next = { lat: lat, lon: lon, grid: g, label: label || "" };
    setLoc(next);
    try { localStorage.setItem(LOC_STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  }, []);

  // Back to the operator's home QTH (profile grid); forget the remembered pick.
  function resetToQth() {
    userPickedRef.current = false;
    try { localStorage.removeItem(LOC_STORAGE_KEY); } catch (e) {}
    if (props.myGrid && isValidGrid(props.myGrid)) {
      var c = gridToLatLon(props.myGrid);
      if (c) { setLoc({ lat: c.lat, lon: c.lon, grid: String(props.myGrid).trim(), label: "" }); return; }
    }
    if (geoInfo && geoInfo.lat) setLoc({ lat: geoInfo.lat, lon: geoInfo.lon, grid: geoInfo.grid || "", label: geoInfo.city || "" });
  }

  // Device GPS — the primary shortcut for operators already in the field (POTA/SOTA).
  function useGps() {
    if (!navigator.geolocation) { setGpsErr(true); return; }
    setGpsBusy(true); setGpsErr(false);
    navigator.geolocation.getCurrentPosition(
      function(pos) {
        setGpsBusy(false);
        pickLoc(pos.coords.latitude, pos.coords.longitude);
      },
      function() {
        setGpsBusy(false);
        setGpsErr(true);
        setTimeout(function() { setGpsErr(false); }, 6000);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }

  // Profile grid may resolve after mount (async /api/profiles). Adopt it as long as
  // the operator hasn't picked their own location yet.
  useEffect(function() {
    if (userPickedRef.current) return;
    if (props.myGrid && isValidGrid(props.myGrid)) {
      var c = gridToLatLon(props.myGrid);
      if (c) setLoc({ lat: c.lat, lon: c.lon, grid: String(props.myGrid).trim(), label: "" });
    }
  }, [props.myGrid]);

  // Keep the grid text box mirroring the active location (updated by map drag / city / GPS).
  useEffect(function() { setGridIn(loc.grid || ""); }, [loc.grid]);

  function onGridChange(v) {
    setGridIn(v);
    var g = String(v || "").trim();
    if (isValidGrid(g)) {
      var c = gridToLatLon(g);
      if (c) pickLoc(c.lat, c.lon, g.toUpperCase(), "");
    }
  }

  function searchCity() {
    var q = cityQ.trim();
    if (!q) return;
    setCitySearching(true); setCityRes(null);
    fetch("/api/geocode?q=" + encodeURIComponent(q))
      .then(function(r) { return r.json(); })
      .then(function(d) { setCityRes(Array.isArray(d) ? d : []); setCitySearching(false); })
      .catch(function() { setCityRes([]); setCitySearching(false); });
  }

  function chooseCity(r) {
    var label = [r.name, r.state, r.country].filter(Boolean).join(", ");
    pickLoc(r.lat, r.lon, "", label);
    setCityRes(null); setCityQ("");
  }

  var fetchAll = useCallback(function() {
    setLoading(true);
    fetch("/api/solar").then(function(r) { return r.json(); })
      .then(function(d) { setSolar(d); })
      .catch(function() {});
    // Location-aware ionosphere (nearest ionosondes) + observed RF activity nearby.
    fetch("/api/propagation?lat=" + loc.lat + "&lon=" + loc.lon)
      .then(function(r) { return r.json(); })
      .then(function(d) { setProp(d && !d.error ? d : null); })
      .catch(function() { setProp(null); });
    fetch("/api/weather?lat=" + loc.lat + "&lon=" + loc.lon)
      .then(function(r) { return r.json(); })
      .then(function(d) { if (!d.error) setWeather(d); })
      .catch(function() {});
    setLastUpdate(new Date());
    setLoading(false);
  }, [loc.lat, loc.lon]);

  useEffect(function() {
    fetchAll();
    var interval = setInterval(fetchAll, 10 * 60 * 1000);
    return function() { clearInterval(interval); };
  }, [loc.lat, loc.lon]);

  // Build the mini map when the panel is open and Leaflet is ready; wire drag + click
  // to pick a location. Collapsing the panel unmounts the container, so the cleanup
  // destroys the map — reopening recreates it at the current location.
  useEffect(function() {
    if (!locOpen || !ready || !window.L || !miniRef.current || miniMapRef.current) return;
    try {
      var map = window.L.map(miniRef.current, { zoomControl: true, attributionControl: false }).setView([loc.lat, loc.lon], 6);
      addDarkBasemap(map, { maxZoom: 18 });
      var marker = window.L.marker([loc.lat, loc.lon], { draggable: true }).addTo(map);
      marker.on("dragend", function() { var ll = marker.getLatLng(); pickLoc(ll.lat, ll.lng); });
      map.on("click", function(e) { marker.setLatLng(e.latlng); pickLoc(e.latlng.lat, e.latlng.lng); });
      miniMapRef.current = map;
      miniMarkerRef.current = marker;
      setTimeout(function() { try { map.invalidateSize(); } catch (e) {} }, 200);
    } catch (e) { console.error("FieldOps mini-map error:", e); }
    return function() {
      if (miniMapRef.current) { try { miniMapRef.current.remove(); } catch (e) {} miniMapRef.current = null; miniMarkerRef.current = null; }
    };
  }, [ready, locOpen]);

  // Move the pin/view when the location changes from the grid box, city search or GPS.
  useEffect(function() {
    if (!miniMapRef.current || !miniMarkerRef.current) return;
    try {
      miniMarkerRef.current.setLatLng([loc.lat, loc.lon]);
      miniMapRef.current.setView([loc.lat, loc.lon]);
    } catch (e) {}
  }, [loc.lat, loc.lon]);

  // Risk calculations
  var mastRisk = useMemo(function() {
    if (!weather || !weather.current) return null;
    var ws = (weather.current.windSpeed || 0) * 3.6; // m/s to km/h
    var gust = (weather.current.windGust || 0) * 3.6;
    var max = Math.max(ws, gust);
    if (max >= 70) return { level: "extreme", color: "#f43f5e" };
    if (max >= 50) return { level: "high", color: "#ff6b35" };
    if (max >= 30) return { level: "moderate", color: "#eab308" };
    return { level: "low", color: "#00ff88" };
  }, [weather]);

  var lightningRisk = useMemo(function() {
    if (!weather || !weather.current) return null;
    var id = weather.current.weatherId;
    // OWM: 200-232 = thunderstorm
    if (id >= 200 && id <= 232) return { level: "extreme", color: "#f43f5e" };
    // Check forecast for upcoming thunderstorms
    if (weather.forecast) {
      var upcoming = false;
      for (var i = 0; i < Math.min(4, weather.forecast.length); i++) {
        var fid = weather.forecast[i].weatherId;
        if (fid >= 200 && fid <= 232) { upcoming = true; break; }
      }
      if (upcoming) return { level: "high", color: "#ff6b35" };
    }
    // Heavy rain + high humidity
    if ((id >= 500 && id <= 531) && weather.current.humidity > 80) return { level: "moderate", color: "#eab308" };
    return { level: "low", color: "#00ff88" };
  }, [weather]);

  // Upcoming weather threats from forecast
  var threats = useMemo(function() {
    if (!weather || !weather.forecast) return [];
    var list = [];
    for (var i = 0; i < weather.forecast.length; i++) {
      var f = weather.forecast[i];
      if (f.weatherId >= 200 && f.weatherId <= 232) {
        var d = new Date(f.dt * 1000);
        list.push(d.toISOString().slice(0, 16).replace("T", " ") + ": " + f.weatherDesc);
      } else if (f.weatherId >= 502 && f.weatherId <= 531) {
        var d2 = new Date(f.dt * 1000);
        list.push(d2.toISOString().slice(0, 16).replace("T", " ") + ": " + f.weatherDesc);
      }
    }
    return list;
  }, [weather]);

  // Band conditions grouped
  var bandGroups = useMemo(function() {
    if (!solar || !solar.bands) return [];
    var groups = {};
    for (var i = 0; i < solar.bands.length; i++) {
      var b = solar.bands[i];
      var key = b.name;
      if (!groups[key]) groups[key] = {};
      groups[key][b.time] = b.condition;
    }
    return Object.keys(groups).map(function(k) { return { name: k, day: groups[k].day || "-", night: groups[k].night || "-" }; });
  }, [solar]);

  // Geomag status text
  var geomagStatus = useMemo(function() {
    if (!solar) return "";
    var gf = (solar.geomagfield || "").toLowerCase();
    if (gf.indexOf("storm") >= 0) return "stormConditions";
    // "inactive" contains "active" — must be tested first, it means a QUIET field
    if (gf.indexOf("inactive") >= 0 || gf.indexOf("quiet") >= 0) return "quietConditions";
    if (gf.indexOf("unsettled") >= 0 || gf.indexOf("active") >= 0) return "unsettledConditions";
    return "quietConditions";
  }, [solar]);

  // Multi-day forecast (aggregate 3h blocks by day)
  var dailyForecast = useMemo(function() {
    if (!weather || !weather.forecast) return [];
    var days = {};
    for (var i = 0; i < weather.forecast.length; i++) {
      var f = weather.forecast[i];
      var d = new Date(f.dt * 1000);
      var key = d.toISOString().slice(0, 10);
      if (!days[key]) days[key] = { date: key, temps: [], winds: [], pops: [], icons: [], descs: [] };
      days[key].temps.push(f.temp);
      days[key].winds.push(f.windSpeed);
      days[key].pops.push(f.pop);
      days[key].icons.push(f.weatherIcon);
      days[key].descs.push(f.weatherDesc);
    }
    return Object.values(days).slice(0, 4).map(function(d) {
      return {
        date: d.date,
        high: Math.round(Math.max.apply(null, d.temps)),
        low: Math.round(Math.min.apply(null, d.temps)),
        maxWind: Math.round(Math.max.apply(null, d.winds) * 3.6),
        maxPop: Math.round(Math.max.apply(null, d.pops) * 100),
        icon: d.icons[0],
        desc: d.descs[0]
      };
    });
  }, [weather]);

  var now = new Date();
  var utcStr = now.toISOString().slice(0, 19).replace("T", " ") + " UTC";

  // Location-aware propagation (nearest-ionosonde model). When there's no usable
  // ionosonde near the location, the global hamqsl table below acts as fallback.
  var localProp = prop && !prop.fallback && prop.local ? prop.local : null;
  var localBands = prop && !prop.fallback && prop.bands ? prop.bands : null;

  // Compact status text: "City (CC) · GRID · lat, lon"
  var locPlace = weather && weather.location ? weather.location.name + " (" + weather.location.country + ")" : (loc.label || "");
  var locParts = [locPlace, (loc.grid || "").toUpperCase(), loc.lat.toFixed(2) + ", " + loc.lon.toFixed(2)].filter(Boolean);

  var pillBtn = { display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "1px solid " + C.bd, color: C.t2, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "monospace", whiteSpace: "nowrap" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Header bar — location moved to the status bar below; only clocks stay here */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>{"🌡️"} {t(lang, "fieldopsTitle")}</h2>
        <div style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 12, color: C.t3, fontFamily: "monospace" }}>
          <span>{"🕒"} {utcStr}</span>
          {lastUpdate && <span style={{ color: C.a1 }}>{t(lang, "lastUpdated")}: {lastUpdate.toLocaleTimeString()}</span>}
        </div>
      </div>

      {/* COMPACT LOCATION BAR — editor expands on demand */}
      <Card style={{ padding: "10px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, fontSize: 13 }}>
            <span>{"📍"}</span>
            <span style={{ fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {locParts.join(" · ")}
            </span>
            {gpsErr && <span style={{ fontSize: 11, color: "#f43f5e" }}>{t(lang, "gpsError")}</span>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={useGps} disabled={gpsBusy} title={t(lang, "useGpsHint")} style={Object.assign({}, pillBtn, gpsBusy ? { opacity: 0.6, cursor: "wait" } : {})}>
              {gpsBusy ? "⏳" : "📡"} {t(lang, "useGps")}
            </button>
            <button onClick={resetToQth} style={pillBtn}>
              {"🏠"} {t(lang, "useMyQth")}
            </button>
            <button onClick={function() { setLocOpen(!locOpen); }}
              style={Object.assign({}, pillBtn, locOpen ? { border: "1px solid " + C.a1, color: C.a1, background: C.a1 + "15" } : {})}>
              {locOpen ? "✕ " + t(lang, "closeLocation") : "✏️ " + t(lang, "changeLocation")}
            </button>
          </div>
        </div>

        {/* Expanded editor: mini map + grid + city search (same handlers as V1) */}
        {locOpen && (
          <div style={{ marginTop: 12, borderTop: "1px solid " + C.bd, paddingTop: 12 }}>
            <div style={{ fontSize: 11, color: C.t3, marginBottom: 10 }}>{t(lang, "fieldopsLocHint")}</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
              {/* Mini map */}
              <div ref={miniRef} style={{ width: "100%", maxWidth: 360, height: 200, borderRadius: 10, border: "1px solid " + C.bd, overflow: "hidden", flex: "1 1 300px" }}>
                {!ready && <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.t3, fontSize: 12 }}>{t(lang, "loading")}</div>}
              </div>
              {/* Text inputs */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: "1 1 220px", minWidth: 200 }}>
                {/* Grid */}
                <div>
                  <div style={{ fontSize: 11, color: C.t3, marginBottom: 4 }}>{"📐"} Grid</div>
                  <input value={gridIn} onChange={function(e) { onGridChange(e.target.value); }} placeholder={t(lang, "enterGrid")}
                    style={{ width: "100%", boxSizing: "border-box", background: C.s1, border: "1px solid " + ((gridIn && !isValidGrid(gridIn.trim())) ? "#f43f5e" : C.bd), color: C.t1, borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "'Courier New',monospace", textTransform: "uppercase" }} />
                  {gridIn && !isValidGrid(gridIn.trim()) && <div style={{ fontSize: 10, color: "#f43f5e", marginTop: 3 }}>{t(lang, "invalidGrid")}</div>}
                </div>
                {/* City */}
                <div>
                  <div style={{ fontSize: 11, color: C.t3, marginBottom: 4 }}>{"🌍"} {t(lang, "searchCity").replace("...", "")}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={cityQ} onChange={function(e) { setCityQ(e.target.value); }}
                      onKeyDown={function(e) { if (e.key === "Enter") searchCity(); }} placeholder={t(lang, "searchCity")}
                      style={{ flex: 1, minWidth: 0, background: C.s1, border: "1px solid " + C.bd, color: C.t1, borderRadius: 6, padding: "8px 10px", fontSize: 13 }} />
                    <button onClick={searchCity} style={{ background: C.a1 + "20", border: "1px solid " + C.a1, color: C.a1, borderRadius: 6, padding: "0 12px", fontSize: 13, cursor: "pointer" }}>{"🔍"}</button>
                  </div>
                  {citySearching && <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>{t(lang, "searchingCity")}</div>}
                  {cityRes && cityRes.length === 0 && !citySearching && <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>{t(lang, "noCityResults")}</div>}
                  {cityRes && cityRes.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, maxHeight: 140, overflowY: "auto" }}>
                      {cityRes.map(function(r, i) {
                        return (
                          <button key={i} onClick={function() { chooseCity(r); }}
                            style={{ textAlign: "left", background: C.s1, border: "1px solid " + C.bd, color: C.t2, borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
                            {[r.name, r.state, r.country].filter(Boolean).join(", ")}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* SECTION A: Solar Propagation */}
      <Card style={{ padding: 18 }}>
        <div style={{ fontWeight: 700, color: C.a4, fontSize: 14, marginBottom: 14 }}>{"☀️"} {t(lang, "solarPropagation")}</div>
        {solar ? (
          <div>
            {/* Solar indices */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
              <StatBox icon={"☀️"} label={t(lang, "solarFlux")} value={solar.solarflux} color={sfiColor(solar.solarflux)} sub={solar.solarflux >= 100 ? t(lang, "condGood") : solar.solarflux >= 70 ? t(lang, "condFair") : t(lang, "condPoor")} tip={t(lang, "tipSolarFlux")} />
              <StatBox icon={"🧲"} label={t(lang, "aIndex")} value={solar.aindex} color={aColor(solar.aindex)} tip={t(lang, "tipAIndex")} />
              <StatBox icon={"🌀"} label={t(lang, "kIndex")} value={solar.kindex} color={kColor(solar.kindex)} tip={t(lang, "tipKIndex")} />
              <StatBox icon={"🔆"} label={t(lang, "sunspots")} value={solar.sunspots} color={C.a1} tip={t(lang, "tipSunspots")} />
              <StatBox icon={"🔊"} label={t(lang, "signalNoise")} value={solar.signalnoise || "-"} color={C.a1} tip={t(lang, "tipSignalNoise")} />
              <StatBox icon={"🧭"} label={t(lang, "geomagField")} value={t(lang, geomagStatus === "quietConditions" ? "geomagQuiet" : geomagStatus === "unsettledConditions" ? "geomagUnsettled" : "geomagStorm")} color={geomagStatus === "quietConditions" ? "#00ff88" : geomagStatus === "unsettledConditions" ? "#eab308" : "#f43f5e"} sub={solar.geomagfield || t(lang, geomagStatus)} tip={t(lang, "tipGeomagField")} />
              {localProp && <StatBox icon={"📶"} label={"MUF(3000)"} value={localProp.mufd + " MHz"} color={mufColor(localProp.mufd)} sub={t(lang, "nearestIonosonde") + " " + String(localProp.station.name).split(",")[0] + " · " + localProp.station.km + " km"} tip={t(lang, "tipMuf")} />}
              {localProp && <StatBox icon={"📈"} label={"foF2"} value={localProp.fof2 + " MHz"} color={C.a1} sub={localProp.daylight ? "☀️ " + t(lang, "bandDay") : "🌙 " + t(lang, "bandNight")} tip={t(lang, "tipFof2")} />}
            </div>

            {/* Band conditions: location-aware when an ionosonde is near, global fallback otherwise */}
            {localBands ? (
              <div>
                <div style={{ fontWeight: 600, color: C.t2, fontSize: 13, marginBottom: 10 }}>{"📻"} {t(lang, "localBandConditions")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "auto repeat(2, 1fr)", gap: 0, background: C.s1, borderRadius: 8, border: "1px solid " + C.bd, overflow: "hidden", fontSize: 12 }}>
                  <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, borderBottom: "1px solid " + C.bd }}>{t(lang, "bandLabel")}</div>
                  <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, textAlign: "center", borderBottom: "1px solid " + C.bd }}>{"⚡"} {t(lang, "bandNow")}</div>
                  <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, textAlign: "center", borderBottom: "1px solid " + C.bd }}>{"📡"} {t(lang, "bandActivity")}</div>
                  {localBands.map(function(b) {
                    return [
                      <div key={b.name + "n"} style={{ padding: "6px 14px", fontWeight: 600, color: C.a1, borderBottom: "1px solid " + C.bd + "40" }}>{b.name}</div>,
                      <div key={b.name + "c"} style={{ padding: "6px 14px", textAlign: "center", fontWeight: 700, color: condColor(b.rating), borderBottom: "1px solid " + C.bd + "40" }}>{b.rating}</div>,
                      <div key={b.name + "a"} style={{ padding: "6px 14px", textAlign: "center", fontWeight: 700, color: activityColor(b.activity), borderBottom: "1px solid " + C.bd + "40" }}>{b.activity > 0 ? b.activity + " spots" : "—"}</div>
                    ];
                  })}
                </div>
                <div style={{ fontSize: 10, color: C.t3, marginTop: 8 }}>
                  {t(lang, "activityFootnote")} &middot; {t(lang, "nearestIonosonde")}: {localProp.station.name} ({localProp.station.km} km &middot; {localProp.station.ageMin} min)
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 600, color: C.t2, fontSize: 13, marginBottom: 10 }}>{"📻"} {t(lang, "bandConditions")}</div>
                {prop && <div style={{ fontSize: 11, color: "#eab308", marginBottom: 8 }}>{"🛰️"} {t(lang, "noIonosondeFallback")}</div>}
                {bandGroups.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "auto repeat(2, 1fr)", gap: 0, background: C.s1, borderRadius: 8, border: "1px solid " + C.bd, overflow: "hidden", fontSize: 12 }}>
                    {/* Header */}
                    <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, borderBottom: "1px solid " + C.bd }}>{t(lang, "bandLabel")}</div>
                    <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, textAlign: "center", borderBottom: "1px solid " + C.bd }}>{"☀️"} {t(lang, "bandDay")}</div>
                    <div style={{ padding: "8px 14px", background: C.s2, fontWeight: 700, color: C.t2, textAlign: "center", borderBottom: "1px solid " + C.bd }}>{"🌙"} {t(lang, "bandNight")}</div>
                    {bandGroups.map(function(bg) {
                      return [
                        <div key={bg.name + "n"} style={{ padding: "6px 14px", fontWeight: 600, color: C.a1, borderBottom: "1px solid " + C.bd + "40" }}>{bg.name}</div>,
                        <div key={bg.name + "d"} style={{ padding: "6px 14px", textAlign: "center", fontWeight: 700, color: condColor(bg.day), borderBottom: "1px solid " + C.bd + "40" }}>{bg.day}</div>,
                        <div key={bg.name + "ng"} style={{ padding: "6px 14px", textAlign: "center", fontWeight: 700, color: condColor(bg.night), borderBottom: "1px solid " + C.bd + "40" }}>{bg.night}</div>
                      ];
                    })}
                  </div>
                )}
              </div>
            )}
            {solar.updated && <div style={{ fontSize: 10, color: C.t3, marginTop: 8 }}>{t(lang, "lastUpdated")}: {solar.updated}</div>}
          </div>
        ) : (
          <div style={{ color: C.t3, fontSize: 13 }}>{loading ? t(lang, "loading") : "No solar data"}</div>
        )}
      </Card>

      {/* ═══ SECTION B: Weather & Field Ops Safety ═══ */}
      {weather && weather.current && (
        <Card style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, color: C.a3, fontSize: 14 }}>{"☁️"} {t(lang, "fieldOpsWeather")} {weather.location ? "@ " + weather.location.name + " (" + weather.location.country + ")" : ""}</div>
            {mastRisk && <span style={{ fontSize: 11, fontWeight: 700, color: mastRisk.level === "low" ? "#00ff88" : mastRisk.level === "moderate" ? "#eab308" : "#f43f5e", background: (mastRisk.level === "low" ? "#00ff88" : mastRisk.level === "moderate" ? "#eab308" : "#f43f5e") + "15", padding: "4px 12px", borderRadius: 6 }}>{t(lang, "fieldOps")}: {mastRisk.level === "low" ? "OK" : t(lang, "riskHigh")}</span>}
          </div>
          <div style={{ fontSize: 11, color: C.t3, marginBottom: 14 }}>{t(lang, "fieldOpsDesc")}</div>

          {/* Weather stats row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 16 }}>
            <StatBox icon={"🌡️"} label={t(lang, "temperature")} value={Math.round(weather.current.temp) + "°C"} color={C.a3} sub={t(lang, "feelsLike") + " " + Math.round(weather.current.feelsLike) + "°C"} />
            <StatBox icon={"💨"} label={t(lang, "windSpeed")} value={Math.round(weather.current.windSpeed * 3.6) + " km/h"} color={C.a1} sub={windDirLabel(weather.current.windDeg) + (weather.current.windGust ? " | " + t(lang, "windGust") + " " + Math.round(weather.current.windGust * 3.6) : "")} />
            <StatBox icon={"💧"} label={t(lang, "humidity")} value={weather.current.humidity + "%"} color="#06b6d4" />
            <StatBox icon={"📊"} label={t(lang, "pressureHpa")} value={weather.current.pressure + " hPa"} color={C.a4} />
            <StatBox icon={"👁️"} label={t(lang, "visibilityLabel")} value={(weather.current.visibility / 1000).toFixed(1) + " km"} color={C.t2} />
          </div>

          {/* Risk cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {mastRisk && (
              <div style={{ background: mastRisk.color + "10", border: "1px solid " + mastRisk.color + "40", borderRadius: 10, padding: 14 }}>
                <div style={{ fontWeight: 700, color: mastRisk.color, fontSize: 13, marginBottom: 6 }}>{"🚧"} {t(lang, "mastRisk")}: {t(lang, "risk" + mastRisk.level.charAt(0).toUpperCase() + mastRisk.level.slice(1))}</div>
                <div style={{ fontSize: 11, color: C.t2 }}>{t(lang, "mast" + mastRisk.level.charAt(0).toUpperCase() + mastRisk.level.slice(1))}</div>
              </div>
            )}
            {lightningRisk && (
              <div style={{ background: lightningRisk.color + "10", border: "1px solid " + lightningRisk.color + "40", borderRadius: 10, padding: 14 }}>
                <div style={{ fontWeight: 700, color: lightningRisk.color, fontSize: 13, marginBottom: 6 }}>{"⚡"} {t(lang, "lightningRisk")}: {t(lang, "risk" + lightningRisk.level.charAt(0).toUpperCase() + lightningRisk.level.slice(1))}</div>
                <div style={{ fontSize: 11, color: C.t2 }}>{t(lang, "light" + lightningRisk.level.charAt(0).toUpperCase() + lightningRisk.level.slice(1))}</div>
              </div>
            )}
          </div>

          {/* Upcoming threats */}
          {threats.length > 0 && (
            <div style={{ background: "#f43f5e10", border: "1px solid #f43f5e30", borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: "#f43f5e", fontSize: 12, marginBottom: 8 }}>{"⚠️"} {t(lang, "upcomingThreats")}</div>
              {threats.map(function(th, i) {
                return <div key={i} style={{ fontSize: 11, color: "#f43f5e", fontFamily: "monospace", marginBottom: 2 }}>{"⚠️"} {th}</div>;
              })}
            </div>
          )}
          {threats.length === 0 && (
            <div style={{ fontSize: 11, color: "#00ff88", marginBottom: 16 }}>{"✅"} {t(lang, "noThreats")}</div>
          )}

          {/* 12h forecast */}
          <div style={{ fontWeight: 600, color: C.t2, fontSize: 13, marginBottom: 10 }}>{"🕒"} {t(lang, "forecast12h")}</div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8 }}>
            {weather.forecast && weather.forecast.map(function(f, i) {
              var d = new Date(f.dt * 1000);
              var hh = ("0" + d.getUTCHours()).slice(-2) + ":00";
              return (
                <div key={i} style={{ minWidth: 72, background: C.s1, border: "1px solid " + C.bd, borderRadius: 8, padding: "8px 6px", textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.a1, marginBottom: 4 }}>{hh}</div>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>{WEATHER_EMOJI[f.weatherIcon] || "☁️"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{Math.round(f.temp)}{"°"}</div>
                  <div style={{ fontSize: 10, color: C.t3 }}>{Math.round(f.windSpeed * 3.6)} km/h</div>
                  <div style={{ fontSize: 10, color: f.pop > 0.5 ? "#06b6d4" : C.t3 }}>{Math.round(f.pop * 100)}%</div>
                </div>
              );
            })}
          </div>

          {/* Multi-day forecast */}
          {dailyForecast.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 600, color: C.t2, fontSize: 13, marginBottom: 10 }}>{"📅"} {t(lang, "forecastDays")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                {dailyForecast.map(function(d) {
                  var dayName = new Date(d.date + "T12:00:00Z").toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { weekday: "long" });
                  return (
                    <div key={d.date} style={{ background: C.s1, border: "1px solid " + C.bd, borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.a1, marginBottom: 4, textTransform: "capitalize" }}>{dayName}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 20 }}>{WEATHER_EMOJI[d.icon] || "☁️"}</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>{d.high}{"°"}/{d.low}{"°"}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.5 }}>
                        {"💨"} {d.maxWind} km/h &middot; {"💧"} {d.maxPop}%
                      </div>
                      <div style={{ fontSize: 10, color: C.t3, marginTop: 2, textTransform: "capitalize" }}>{d.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {!weather && !loading && (
        <Card style={{ padding: 18 }}>
          <div style={{ color: C.t3, fontSize: 13 }}>{"☁️"} {t(lang, "loading")}</div>
        </Card>
      )}
    </div>
  );
}
